<script setup>
import MessageInput from "@/components/chat/messages/MessageInput.vue";
import ChatContent from "@/components/chat/messages/ChatContent.vue";
import SampleMessages from "@/components/chat/messages/SampleMessages.vue";
import RecommendedButtons from "@/components/chat/messages/RecommendedButtons.vue";
import Disclaimer from "@/components/chat/Disclaimer.vue";

import axios from "axios";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useChatCommands } from "@/utils/chatCommands/useChatCommands";
import parseToXml from "@/utils/jsonParser/parseToXml";
import isJson from "@/utils/checkIsJSON";

import { useChatToken } from "../chat/useChatToken";
import { useChatWebSocket } from "../chat/useChatWebSocket";
import { useHelpUser } from "../chat/useHelpUser";

const { currentDocument = null } = defineProps(["currentDocument"]);

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const isChatDisabled = ref(false);
const isDisclaimerVisible = ref(false);
const isSecodnDisclaimerVisible = ref(true);
const isResponseLoading = computed(() => store.state.chat.isResponseLoading);
const isWizardComleted = computed(() => store.state.editor.wizard.isCompleted);

const messages = ref([]);

const user = computed(() => store.state.auth.user);
const state = computed(() => {
  const documentContent = currentDocument?.content;
  if (!documentContent) {
    return "general";
  }

  if (!isWizardComleted.value) {
    return "editing_blur_full";
  }

  if (!currentDocument.is_paid && !user.value.hasValidSubscription) {
    return "editing_blur_partial";
  }

  return "editing_full";
});

const chatId = computed(() =>
  state.value.includes("editing") && currentDocument.id ? currentDocument.id : user.value.id,
);
const page = ref(1);

const createWelcomeMessage = () => {
  return [
    {
      id: crypto.randomUUID(),
      source: "bot",
      message:
        state.value === "general"
          ? t("chat.new_document_welcome_message")
          : t("chat.creator_welcome_message"),
    },
  ];
};

const handleScrollPosition = async (currentPage) => {
  const chatContent = chatContentREF.value;
  if (!chatContent) return;

  if (currentPage === 1) {
    await nextTick();
    scrollToBottom();
  } else {
    const lastHeight = chatContent.scrollHeight;
    await nextTick();
    const newHeight = chatContent.scrollHeight;
    chatContent.scrollTop = newHeight - lastHeight;
  }
};

const getChatHistory = async (event) => {
  const isDocumentChat = !!currentDocument;
  const url = isDocumentChat
    ? `api/documents/${currentDocument?.id}/chat-messages`
    : `api/chat-messages`;

  try {
    const response = await axios.get(url, { params: { page: page.value } });
    const { data: messagesList, current_page, next_page_url } = response.data;

    if (messagesList.length === 0 && page.value === 1) {
      messages.value = createWelcomeMessage();
    } else {
      messages.value.unshift(...messagesList);
    }

    await handleScrollPosition(current_page);

    if (!next_page_url) {
      event?.done("empty");
    } else {
      await event?.done();
      page.value++;
    }
  } catch (error) {
    console.error("Error getting messages history", error);
  }
};

const saveMessageOnServer = async (messageContent, source = "user") => {
  const body = {
    chat_id: chatId.value,
    message: messageContent,
    source,
  };

  const apiHost = `${import.meta.env.VITE_APP_API_BASE_URL}/api`;
  const documentChatURL = `${apiHost}/documents/${currentDocument?.id}/chat-messages`;
  const generalChatURL = `${apiHost}/chat-messages`;

  await axios.post(currentDocument ? documentChatURL : generalChatURL, body);
};

const showDisclaimer = () => {
  isDisclaimerVisible.value = true;
};

const setSecondDisclaimerVisibility = (state) => {
  isSecodnDisclaimerVisible.value = state;
};

const { token, tokenExpired, getChatToken } = useChatToken(chatId);
const { socket, setupWebsocket } = useChatWebSocket(
  token,
  messages,
  store,
  t,
  saveMessageOnServer,
  showDisclaimer,
);
const { handleNoAction } = useHelpUser(
  currentDocument,
  store,
  token,
  chatId,
  state,
  socket,
  isJson,
  parseToXml,
);

defineExpose({
  handleNoAction,
});

const handleRecommendedButtonClick = async (clickedButton) => {
  console.log("Clicked button: ", clickedButton);
  if (clickedButton.type === "create_document") {
    const response = await axios.post("api/documents", {
      template_id: clickedButton.template_uuid,
    });

    const documentId = response.data.data.id;

    store.commit("editor/updateDocumentId", documentId);
    store.commit("chat/updateSessionId", documentId);

    router.push({
      path: '/document-questions',
      query: {
        template: clickedButton.template_uuid,
        documentId: documentId
      }
    });
  }
};

//work in progress (waiting for awakast). do not remove
// const disableChat = () => {
//   isChatDisabled.value = true;
//   messages.value.push({
//     id: crypto.randomUUID(),
//     source: "bot",
//     message:
//       state.value === "editing"
//         ? t("errors.chat.limit_document")
//         : t("errors.chat.limit_general"),
//   });
// };

const handleSendMessage = async (messageContent) => {
  console.log("Sending messaage with content: " + messageContent);
  const isEmpty = messageContent.trim() === "";

  if (isEmpty || !socket.value) return;

  useChatCommands({
    command: "remove_selection_from_paragraphs",
  });

  saveMessageOnServer(messageContent);

  const messageId = crypto.randomUUID();
  const documentContent =
    store.state.editor.content.content ||
    (store.state.editor.content ? store.state.editor.content : currentDocument?.content);

  const metadata = currentDocument
    ? {
        document: isJson(documentContent)
          ? parseToXml(documentContent, currentDocument?.meta)
          : documentContent,
        template_uuid: currentDocument ? currentDocument?.template_awakast_id : null,
      }
    : {};

  //ADD WIZARD DATA TO META
  if (state.value.includes("editing")) {
    const stepID = store.state.editor?.wizard?.currentStep || null;
    const variableId = store.state.editor?.editableVariable?.id || null;
    stepID && (metadata.step_id = stepID);
    variableId && (metadata.variable_id = variableId);
  }

  const message = {
    token: token.value,
    message_id: messageId,
    document_id: currentDocument ? currentDocument.id : null,
    chat_id: chatId.value,
    input: messageContent,
    state: { state: state.value },
    metadata: metadata,
    template_title: currentDocument ? currentDocument?.meta?.title : null,
    template_uuid: currentDocument ? currentDocument?.template_id : null,
  };

  const simpleMessage = {
    id: messageId,
    source: "user",
    message: messageContent,
  };
  messages.value.push(simpleMessage);

  console.log("message", message);
  store.commit("chat/updateIsResponseLoading", true);
  socket.value.send(JSON.stringify(message));

  console.log("Sending successful");
};

const chatContentREF = ref(null);
const scrollToBottom = () => {
  const chatContent = chatContentREF.value;

  if (chatContent) {
    chatContent.scrollTop = chatContent.scrollHeight;
  }
};

watch(
  () => store.state.auth.user,
  async () => {
    if (store.state.auth.user.id) {
      const isExpired = tokenExpired.value < Date.now();
      if (!token.value || isExpired) {
        try {
          await getChatToken();
        } catch (error) {
          toast.error(t("notifications.chat_char_limit"));
          isChatDisabled.value = true;
        }
      }
    }
  },
  { immediate: true },
);

watch(
  () => messages.value,
  async () => {
    await nextTick();
    const chatContent = chatContentREF.value;
    if (chatContent) {
      scrollToBottom();
    }
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  store.commit("chat/resetButtons");
  setupWebsocket();
});

onUnmounted(() => {
  if (socket.value) {
    socket.value.close();
  }
});
</script>

<template>
  <div class="chat-container" ref="containerREF">
    <div class="content-container">
      <div class="content" ref="chatContentREF">
        <ChatContent
          :messages="messages"
          @load="getChatHistory"
          v-if="!isSecodnDisclaimerVisible"
        />
        <Disclaimer
          v-if="isSecodnDisclaimerVisible"
          :user="user"
          @setVisibility="setSecondDisclaimerVisibility"
        />
      </div>

      <div class="message-input-container" v-if="!isSecodnDisclaimerVisible">
        <SampleMessages
          @clickMessage="handleSendMessage"
          v-if="messages.length <= 1 && state.includes('editing')"
        />
        <RecommendedButtons @click-button="handleRecommendedButtonClick" />
        <MessageInput
          :chat-disabled="isChatDisabled || isResponseLoading"
          @sendMessage="handleSendMessage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.chat-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  --site-header-height: 60px;
  --page-header-height: 103px;
  --botom-menu: 75px;
  --page-padding-top: 30px;
  height: calc(
    100vh - var(--site-header-height) - var(--page-header-height) - var(--page-padding-top) - var(
        --botom-menu
      )
  );

  .chat-header {
    display: flex;
    width: 100%;
    background-color: var(--light-grey);
    padding: 10px 10px 10px 48px;

    .chat-title {
      font-family: Archivo, sans-serif;
      font-weight: 600;
      color: var(--blue-bonnet);
      width: 90%;
      margin-left: 15px;
    }

    .hide-button {
      width: 100px;
      margin-right: 9px;
      color: white;
      background-color: var(--blue-bonnet);
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    height: 100%;
    background-color: white;
    transition: 0.3s ease-in-out;
    overflow: hidden;

    &.isHidden {
      border: none;
      height: 0px;
    }

    .content {
      width: 100%;
      overflow-y: auto;
      padding: 0 0 20px;
    }

    .message-input-container {
      width: 100%;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 15px;
      margin-bottom: 25px;
    }
  }
}
</style>
